import type { MessageKey } from './en.js';

/** Español. El idioma de la brigada: partida, cubiertos, mise en place. */
export const es: Record<MessageKey, string> = {
  'common.loading': 'Cargando…',
  'common.save': 'Guardar',
  'common.saving': 'Guardando…',
  'common.saved': 'Guardado.',
  'common.cancel': 'Cancelar',
  'common.edit': 'Editar',
  'common.delete': 'Eliminar',
  'common.reallyDelete': '¿Eliminar de verdad?',
  'common.remove': 'Quitar',
  'common.reallyRemove': '¿Quitar de verdad?',
  'common.add': 'Añadir',
  'common.create': 'Crear',
  'common.creating': 'Creando…',
  'common.none': 'Ninguna',
  'common.anyone': 'Cualquiera',
  'common.name': 'Nombre',
  'common.email': 'Correo',
  'common.password': 'Contraseña',
  'common.station': 'Partida',
  'common.anyStation': 'Cualquier partida',
  'common.anywhere': 'Sin partida',
  'common.optional': 'Opcional',
  'common.moveUp': 'Subir',
  'common.moveDown': 'Bajar',
  'common.unit': 'Unidad',
  'common.howMuch': 'Cuánto',
  'common.amount': 'Cantidad',
  'common.working': 'Calculando…',
  'common.dismiss': 'Cerrar',
  'common.tryAgain': 'Reintentar',
  'common.loadFailed': 'No se ha podido cargar. Revisa la conexión y vuelve a intentarlo.',

  'error.generic': 'Algo ha fallado.',
  'error.notFound': 'Eso ya no está. Puede que alguien lo haya quitado.',
  'error.validationFailed': 'Algo no está bien rellenado. Revísalo y vuelve a intentarlo.',
  'error.alreadyExists': 'Eso ya existe.',
  'error.stillReferenced': 'Todavía se usa en otro sitio, así que no se puede quitar.',
  'error.invalidData': 'Ese valor no se permite aquí.',
  'error.writeInProgress': 'Ese cambio se está guardando. Vuelve a intentarlo en un momento.',
  'error.backupFileMissing': 'Adjunta el archivo de copia de seguridad.',
  'error.onedriveNotConnected': 'Conecta OneDrive primero.',
  'error.nasAddressNeeded': 'Escribe la dirección del NAS al que enviar las copias.',
  'error.fileTooLarge': 'Ese archivo es demasiado grande.',
  'error.unknownTimeZone': 'Este servidor no conoce esa zona horaria.',
  'error.alreadySetUp': 'Este servidor ya está configurado.',
  'error.imageType': 'Usa una imagen JPEG, PNG o WebP.',
  'error.imageMissing': 'No se ha adjuntado ninguna imagen.',
  'error.notADish': 'Solo un plato tiene ficha de plato.',
  'error.logoTooLarge': 'Ese logotipo ocupa más de 2 MB.',
  'error.photoTooLarge': 'Esa imagen ocupa más de 8 MB.',
  'error.taskNotYours': 'Solo puedes poner tu propio nombre en una línea.',
  'error.taskChefOnly':
    'Puedes empezar y terminar una línea, pero solo un chef puede cambiar lo que dice.',
  'error.staffRank': 'Solo un administrador puede gestionar personal de tu rango o superior.',
  'error.adminNeedsPassword': 'Un administrador necesita correo y contraseña, no solo un PIN.',
  'error.passwordNeedsEmail': 'Entrar con contraseña necesita un correo electrónico.',
  'error.noWayToSignIn': 'Se quedaría sin forma de entrar. Dale una contraseña o un PIN.',
  'error.emailTaken': 'Alguien ya tiene ese correo electrónico.',
  'error.lastAdmin':
    'Es el único administrador que queda. Haz administrador a otra persona primero, o nadie podrá gestionar esta cocina.',
  'error.cannotRemoveSelf': 'No puedes quitar tu propia cuenta.',
  'error.onlyDishes': 'En un menú solo van platos: {names}.',
  'error.menuNameTaken': 'Ya hay un menú con ese nombre.',
  'error.menuUnavailable':
    'No se pueden calcular los alérgenos de {names}, porque apunta a una receta o ingrediente que ya no existe. Arregla primero la receta.',
  'error.recipeGone': 'Esa receta ya no existe.',
  'error.recipeLoop':
    '{parent} no puede contener {child}, porque {child} ya contiene {parent}. Sería un bucle.',
  'error.scaleFailed': 'Esa receta no se pudo calcular a ese tamaño.',
  'error.scaleUnits': 'Ese tamaño está en otro tipo de unidad que lo que rinde la receta.',
  'error.scaleLoop': 'Esta receta se contiene a sí misma, así que no se puede escalar.',
  'error.scaleNoPortions':
    'Esta receta no está escrita en raciones. Escálala por tanda o por rendimiento.',
  'error.stillUsed': 'Todavía lo usa {names}. Quítalo de ahí primero.',
  'error.alreadyInside': 'Este dispositivo está en la red de la cocina y no necesita aprobación.',
  'error.allergensUnknown':
    'No se pueden calcular los alérgenos de esta receta: falta algo o hace referencia a sí misma. Revisa lo que lleva en el editor.',
  'error.nameOutsideKitchen':
    'El certificado de la cocina solo vale para la red de la cocina: direcciones como 192.168.1.50 y nombres que acaban en .local, .lan, .home o .internal. «{name}» no es uno de ellos.',
  'error.invalidCredentials': 'No coincide. Inténtalo de nuevo.',
  'error.accountInactive': 'Esa cuenta ya no está activa.',
  'error.deviceNotEnrolled':
    'El acceso con PIN solo funciona en una tablet registrada por un administrador.',
  'error.notAuthenticated': 'Inicia sesión para continuar.',
  'error.forbidden': 'Tu rol no lo permite.',
  'error.setupToken':
    'Ese código de configuración no es correcto. Revisa el enlace que apareció al instalar Mise.',
  'error.accountLocked': 'Demasiados intentos fallidos. Vuelve a intentarlo en 15 minutos.',
  'error.accountLockedUntil':
    'Demasiados intentos fallidos. Vuelve a intentarlo después de las {time}.',

  'shell.unreachableTitle': 'No se puede conectar con el servidor',
  'shell.unreachableBody':
    'Comprueba que el servidor de Mise está en marcha y que este dispositivo está en la misma red.',
  'shell.home': 'Mise, hoy',
  'shell.signOut': 'Cerrar sesión',
  'shell.signOutFailed':
    'No se ha podido cerrar la sesión. Revisa la conexión y vuelve a intentarlo.',
  'shell.sourceCode': 'Código fuente',
  'shell.language': 'Idioma',
  'shell.languageKitchen': 'Idioma de la cocina ({language})',

  'nav.today': 'Hoy',
  'nav.recipes': 'Recetas',
  'nav.dishes': 'Platos',
  'nav.ingredients': 'Ingredientes',
  'nav.settings': 'Ajustes',
  'nav.updateAvailable': 'Hay una versión más nueva de Mise',

  'role.admin': 'Administrador',
  'role.head_chef': 'Jefe de cocina',
  'role.sous_chef': 'Segundo de cocina',
  'role.cook': 'Cocinero',

  'recipeType.dish': 'Plato',
  'recipeType.prep': 'Elaboración',
  'recipeType.component': 'Componente',

  'recipeStatus.draft': 'Borrador',
  'recipeStatus.published': 'Publicada',
  'recipeStatus.archived': 'Archivada',

  'allergen.gluten': 'Cereales que contienen gluten',
  'allergen.crustaceans': 'Crustáceos',
  'allergen.eggs': 'Huevos',
  'allergen.fish': 'Pescado',
  'allergen.peanuts': 'Cacahuetes',
  'allergen.soybeans': 'Soja',
  'allergen.milk': 'Leche',
  'allergen.nuts': 'Frutos de cáscara',
  'allergen.celery': 'Apio',
  'allergen.mustard': 'Mostaza',
  'allergen.sesame': 'Granos de sésamo',
  'allergen.sulphites': 'Dióxido de azufre y sulfitos',
  'allergen.lupin': 'Altramuces',
  'allergen.molluscs': 'Moluscos',

  'today.night': 'Noche',
  'today.day': 'Día',
  'today.progress': '{done} de {total} hechos',
  'today.progressLabel': 'Mise en place de {period} hecha',
  'today.mine': 'Lo mío',
  'today.all': 'Todo',
  'today.nothingMine': 'No hay nada tuyo en esta lista.',
  'today.nothingStation': 'Nada en esta partida.',
  'today.pending_one': 'Una marca todavía no ha llegado al servidor.',
  'today.pending_other': '{count} marcas todavía no han llegado al servidor.',
  'today.pendingStill': 'Reintentando. Se guardan en esta tableta hasta que se envíen.',
  'today.tickRefused': 'Una marca no se ha guardado: {reason}',

  'task.doneBy': 'Hecha por {name}',

  'line.loading': 'Cargando la brigada…',
  'line.notEnrolledTitle': 'Esta tablet no está registrada',
  'line.notEnrolledBody':
    'El acceso con PIN solo funciona en una tablet configurada por un administrador. Pídele que inicie sesión aquí y la registre desde Ajustes.',
  'line.signInPassword': 'Entrar con contraseña',
  'line.whoIsOn': '¿Quién está?',
  'line.nobodyHasPin':
    'Nadie tiene PIN todavía. Un administrador puede crear uno para cada cocinero en Ajustes.',
  'line.notOnList': '¿No estás en la lista, o estás en un portátil?',
  'line.signInEmail': 'Entrar con correo y contraseña',
  'line.signingIn': 'Entrando',
  'line.checking': 'Comprobando…',
  'line.notMe': 'No soy yo',

  'login.title': 'Entra en tu cocina',
  'login.subtitle': 'Chefs y responsables, desde cualquier dispositivo.',
  'login.signIn': 'Entrar',
  'login.signingIn': 'Entrando…',
  'login.onTheLine': '¿Estás en la línea?',
  'login.pinLink': 'Entrar con tu PIN',
  'login.eyebrow': 'Correo y contraseña',
  'login.titleKitchen': 'Entrar en {kitchen}',
  'login.show': 'Mostrar',
  'login.hide': 'Ocultar',
  'login.forgot': '¿Has olvidado la contraseña? Un administrador puede poner una nueva en Ajustes.',
  'login.tabletBody':
    'Los cocineros entran en la tableta de cocina: toca tu nombre y luego tu PIN.',
  'login.server': 'Conectado a {host}',
  'line.eyebrow': 'Tableta de cocina',
  'line.tapNamePin': 'Toca tu nombre y luego tu PIN.',
  'line.enterPin': 'Introduce tu PIN.',
  'line.digitsTyped_one': '{count} dígito introducido',
  'line.digitsTyped_other': '{count} dígitos introducidos',
  'line.welcome': 'Hola, {name}.',
  'line.ok': 'OK',
  'line.clear': 'Borrar',

  'setup.title': 'Configurar esta cocina',
  'setup.body':
    'Este servidor está vacío. Ponle nombre a tu cocina y crea la primera cuenta de administrador. Solo verás esta página una vez.',
  'setup.kitchenName': 'Nombre de la cocina',
  'setup.kitchenPlaceholder': 'El León de Oro',
  'setup.yourName': 'Tu nombre',
  'setup.yourNamePlaceholder': 'Como te conoce la brigada',
  'setup.passwordHint': 'Al menos 12 caracteres.',
  'setup.passwordShort': 'Todavía tiene menos de 12 caracteres.',
  'setup.failed': 'La configuración ha fallado. Revisa el registro del servidor.',
  'setup.create': 'Crear la cocina',
  'setup.codeHint':
    'Configurar desde fuera de la red de la cocina pide este código. Es SETUP_TOKEN en la configuración de Mise, o el final del enlace que mostró el instalador. Si no se definió ninguno, Mise creó uno y lo escribió en su registro.',
  'setup.codeLabel': 'Código de configuración',
  'setup.stepKitchen': 'Restaurante',
  'setup.stepYou': 'Tú',
  'setup.stepBrigade': 'Partidas y equipo',
  'setup.stepRecipes': 'Recetas',
  'setup.stepSettings': 'Ajustes',
  'setup.stepCheck': 'Revisión',
  'setup.stepOf': 'Paso {current} de {total}',
  'setup.stepShort': 'Paso {current} / {total}',
  'setup.next': 'Siguiente',
  'setup.back': 'Atrás',
  'setup.skip': 'Saltar por ahora',
  'setup.optional': 'Opcional',
  'setup.required': 'Rellena esto primero.',
  'setup.kitchenTitle': 'Pon nombre al restaurante',
  'setup.kitchenBody':
    'Este nombre aparece arriba en cada pantalla. El idioma pasa a ser el de la cocina; cada persona puede elegir el suyo.',
  'setup.youTitle': 'Tu cuenta',
  'setup.youBody':
    'Eres el administrador. Entras con este correo y contraseña, desde cualquier dispositivo.',
  'setup.emailInvalid': 'Eso no parece una dirección de correo.',
  'setup.confirmPassword': 'Repite la contraseña',
  'setup.showPassword': 'Mostrar contraseña',
  'setup.passwordMismatch': 'Las dos contraseñas no coinciden.',
  'setup.passwordMatch': 'Las contraseñas coinciden.',
  'setup.brigadeTitle': 'Partidas y equipo',
  'setup.brigadeBody':
    'Las partidas organizan cada lista de mise en place. Empieza con las que tienes; podrás renombrarlas o añadir más después.',
  'setup.stationsHeading': 'Partidas',
  'setup.stationsEmpty': 'Añade al menos una partida.',
  'setup.stationDuplicate': 'Dos partidas tienen el mismo nombre.',
  'setup.staffHeading': 'Equipo',
  'setup.staffBody':
    'Los cocineros entran en una tableta de cocina tocando su nombre e introduciendo su PIN. Los responsables pueden recibir correo y contraseña más tarde en Ajustes.',
  'setup.staffEmpty': 'Nadie más todavía. Añade tu brigada ahora, o más tarde en Ajustes.',
  'setup.addPerson': 'Añadir a alguien',
  'setup.nameMissing': 'Cada fila necesita un nombre.',
  'setup.pinInvalid': 'Un PIN tiene de 4 a 8 cifras.',
  'setup.yourPin': 'Tu PIN (opcional)',
  'setup.yourPinHint':
    'Para entrar en la tableta de la cocina como el resto de la brigada. Déjalo vacío para saltarlo; puedes ponerlo más tarde.',
  'setup.summaryPin': 'PIN para la tableta de la cocina',
  'setup.backupsTitle': 'Copias de seguridad',
  'setup.backupsHint':
    'Mise hace cada noche una copia de toda la cocina. Una copia en otro sitio evita que un disco roto o un robo se lleve también las copias. Puedes cambiarlo más tarde en Configuración.',
  'setup.backupsTestFirst':
    'Prueba primero la conexión con el NAS, o elige otro sitio para las copias.',
  'setup.backupsConnectFirst': 'Conecta primero OneDrive, o elige otro sitio para las copias.',
  'setup.codeForBackups':
    'Necesario para probar el NAS o conectar OneDrive desde fuera de la red de la cocina. Es SETUP_TOKEN en la configuración de Mise, o el código que Mise escribió en su registro.',
  'setup.recipesTitle': 'Recetas',
  'setup.recipesBody':
    'Añade al recetario los platos y la mise en place que ya conoces: un nombre y lo que rinde una tanda. Ingredientes y elaboración pueden venir después.',
  'setup.recipesEmpty':
    'Aún no hay recetas. Puedes saltar este paso y añadirlas más tarde en el recetario.',
  'setup.addRecipe': 'Añadir una receta',
  'setup.recipeName': 'Nombre de la receta',
  'setup.recipeType': 'Tipo',
  'setup.makes': 'Una tanda rinde',
  'setup.recipeDuplicate': 'Dos recetas tienen el mismo nombre.',
  'setup.yieldInvalid': 'Una tanda tiene que rendir más de 0.',
  'setup.settingsTitle': 'Cómo funciona esta cocina',
  'setup.settingsBody': 'Todo esto se puede cambiar más tarde en Ajustes.',
  'setup.thisDevice': 'Este dispositivo',
  'setup.enrolDevice': 'Usar este dispositivo como tableta de cocina',
  'setup.enrolHint':
    'El equipo podrá entrar en él con su PIN. Déjalo desactivado si es tu propio móvil u ordenador.',
  'setup.deviceName': 'Nombre de la tableta',
  'setup.checkTitle': 'Revisar y crear',
  'setup.checkBody':
    'No se guarda nada hasta que pulses crear. Vuelve a cualquier paso para cambiarlo.',
  'setup.summaryNone': 'Ninguno',
  'setup.summaryAdmin': '{name} · administrador',
  'setup.summaryTablet': 'Registrada como «{name}»',
  'setup.summaryNoTablet': 'No es una tableta de cocina',
  'setup.defaultLarder': 'Cuarto frío',
  'setup.defaultSauce': 'Salsas',
  'setup.defaultSaute': 'Salteado',
  'setup.defaultGrill': 'Parrilla',
  'setup.defaultPastry': 'Pastelería',
  'welcome.step0': 'Bienvenida',
  'welcome.eyebrow': 'Servidor nuevo · nada configurado todavía',
  'welcome.headline': 'Tus recetas y tu mise en place, en tu propio servidor.',
  'welcome.lead1': 'Este es un servidor Mise nuevo. Configurar la cocina lleva unos cinco minutos.',
  'welcome.lead2': 'Nada se guarda hasta el último paso, y todo se puede cambiar después.',
  'welcome.f1t': 'Fichas técnicas',
  'welcome.f1b':
    'Ajusta al rendimiento o al número de comensales, monta los platos con subrecetas y ve los alérgenos de cada subreceta.',
  'welcome.f2t': 'Una lista de mise en place para la cocina',
  'welcome.f2b':
    'Los chefs añaden lo que hay que preparar, por partida. Las líneas marcadas desaparecen solas dos horas después.',
  'welcome.f3t': 'La brigada trabaja con tableta',
  'welcome.f3b':
    'Los cocineros marcan un nombre y un PIN, tachan la mise en place y reclaman una tarea, para que nada se haga dos veces. Cada pantalla se actualiza en vivo.',
  'welcome.stepsLabel': 'Configuración · seis pasos',
  'welcome.duration': 'Unos 5 minutos',
  'welcome.readyLabel': 'Ten a mano',
  'welcome.r1': 'El nombre del restaurante',
  'welcome.r2': 'Un correo y una contraseña para ti, el administrador',
  'welcome.r3': 'Tus partidas — cuarto frío, salsero, pastelería, pase',
  'welcome.r4': 'Opcionalmente tu equipo, un PIN de 4 a 8 dígitos cada uno, y algunas recetas',
  'welcome.changeNote': 'Omite lo que no tengas a mano. Nada es definitivo.',
  'welcome.selfHost':
    'Todo lo que escribas se queda en este servidor. No se crea ninguna cuenta en otro sitio, y Mise no informa a nadie.',
  'welcome.cta': 'Configurar esta cocina',
  'setup.readyLabel': 'Listo',
  'setup.readyEyebrow': 'Configuración completada',
  'setup.readyTitle': '{kitchen} está lista.',
  'setup.readyBody': 'Abriendo la cocina…',
  'tour.label': 'Recorrido',
  'tour.promptEyebrow': 'Hola, {name}',
  'tour.promptTitle': '¿Un recorrido rápido?',
  'tour.promptBody':
    'Un minuto: dónde está tu lista de mise en place, cómo tachar y reclamar tareas, y dónde están las recetas.',
  'tour.promptNote': 'Solo te lo preguntamos una vez.',
  'tour.start': 'Enséñamelo',
  'tour.notNow': 'Ahora no',
  'tour.skip': 'Saltar el recorrido',
  'tour.finish': 'Listo',
  'tour.welcomeTitle': 'Esto es {kitchen}',
  'tour.welcomeBody':
    'Mise reúne las recetas y la mise en place de la cocina en un solo sitio, en tu propio servidor. Aquí está todo.',
  'tour.todayBody':
    'Todo lo que hay que preparar hoy, por partida. Es la página con la que trabaja la línea.',
  'tour.stationsTitle': 'Tu partida',
  'tour.stationsBody': 'Filtra la lista por una partida, o solo por las tareas con tu nombre.',
  'tour.tickTitle': 'Tacharlo',
  'tour.tickBody':
    'Toca una línea cuando esté hecha. Se vuelve gris en todas las pantallas al instante.',
  'tour.makingBody':
    '¿Empiezas algo? Toca primero la marca pequeña, para que nadie más prepare lo mismo.',
  'tour.addLineTitle': 'Añadir a la lista',
  'tour.addLineBody':
    'La mise en place se añade a mano a la lista continua. Si añades lo mismo dos veces, se suma en una sola línea.',
  'tour.recipesBody':
    'Todas las recetas de la cocina, con sus alérgenos. Ajusta las cantidades, o imprímelas para el pase.',
  'tour.ingredientsBody': 'Con lo que se hacen las recetas, con alérgenos y mermas.',
  'tour.settingsBody':
    'Partidas, equipo y PIN, tabletas de cocina, fichas de plato y copias de seguridad.',
  'tour.accountTitle': 'Tu idioma',
  'tour.accountBody': 'Elige aquí tu idioma, y cierra sesión al terminar el turno.',
  'tour.doneTitle': 'Fin del recorrido',
  'tour.doneBody': 'Todo lo demás está en la barra de arriba.',

  'task.remove': 'Quitar {task} de la lista',
  'task.removeConfirm': '¿Quitar?',

  'addTask.what': 'Qué hacer',
  'addTask.placeholder': 'Chalotas en brunoise',
  'addTask.submit': 'Añadir la línea',
  'addTask.fromBook': 'Del recetario',
  'addTask.makes': 'rinde {amount}',
  'addTask.linked': 'Vinculada a la receta {name}. Quien la haga abre la receta en esta cantidad.',
  'task.recipe': 'Receta',
  'task.openRecipe': 'Abrir la receta de {task}',

  'recipes.subtitle':
    'Mise en place y componentes. Usa Sin para ver lo que puede tomar una mesa con alergias, subrecetas incluidas. Los platos tienen su propia pestaña.',
  'recipes.search': 'Buscar en el recetario',
  'recipes.sort': 'Tipo',
  'recipes.anySort': 'Cualquier tipo',
  'recipes.prep': 'Elaboraciones',
  'recipes.components': 'Componentes',
  'recipes.freeFrom': 'Sin…',
  'recipes.freeFromOption': 'Sin {allergen}',
  'recipes.clearFreeFrom': 'Dejar de filtrar por alérgeno',
  'recipes.colSortStation': 'Tipo · partida',
  'recipes.colMakes': 'Rinde',
  'recipes.noMatch': 'Nada en el recetario coincide.',
  'recipes.empty':
    'El recetario está vacío. Empieza con un fondo o una salsa, y luego los platos que los usan.',
  'recipes.noStation': 'sin partida',
  'recipes.noneInTree': 'Sin {allergen}, subrecetas incluidas',
  'recipes.portions_one': '{count} ración',
  'recipes.portions_other': '{count} raciones',
  'recipes.countFreeFrom': '{shown} de {total} recetas sin {allergen}',
  'recipes.countFiltered': '{shown} de {total} recetas',
  'recipes.count_one': '{count} receta en el recetario',
  'recipes.count_other': '{count} recetas en el recetario',
  'recipes.new': 'Nueva receta',

  'recipe.back': '← El recetario',
  'recipe.backToBook': 'Volver al recetario',
  'recipe.makes': 'Rinde {amount}',
  'recipe.minutes': '{count} min',
  'recipe.keeps_one': 'Aguanta {count} hora',
  'recipe.keeps_other': 'Aguanta {count} horas',
  'recipe.asWritten': 'Tal como está escrita',
  'recipe.scaled': '{factor}× la receta escrita',
  'recipe.contains': 'Contiene',
  'recipe.nothingDeclarable': 'Nada que declarar',
  'recipe.mayContain': 'Puede contener {allergens}.',
  'recipe.workedOut': 'Calculado a partir de los ingredientes y de cada subreceta.',
  'recipe.empty': 'Esta receta todavía está vacía.',
  'recipe.method': 'Elaboración',
  'recipe.storage': 'Conservación',
  'recipe.print': 'Imprimir ficha de receta',
  'recipe.duplicate': 'Duplicar',
  'recipe.copyName': '{name} (copia)',
  'recipe.usedBy': 'Se usa en',
  'recipe.level': 'Niv',
  'recipe.what': 'Qué',
  'recipe.open': 'abrir',

  'scale.ask': 'Pide un tamaño',
  'scale.portions': 'Raciones',
  'scale.need': 'Necesito',
  'scale.batches': 'Tandas',

  'plating.title': 'Cómo sale',
  'plating.alt': 'Cómo sale el plato',
  'plating.remove': 'Quitar la foto',
  'plating.empty': 'Todavía no hay foto. El plato que copia un cocinero va aquí, de frente.',
  'plating.uploading': 'Subiendo…',
  'plating.add': 'Añadir una foto del emplatado',
  'plating.addAnother': 'Añadir otra foto',

  'print.whatGoesIn': 'Qué lleva',

  'editor.editing': 'Editando',
  'editor.name': 'Nombre de la receta',
  'editor.namePlaceholder': 'Velouté de pollo',
  'editor.status': 'Estado',
  'editor.lastSaved': 'Guardada {time}',
  'editor.notSaved': 'Todavía sin guardar',
  'editor.colSort': 'Tipo',
  'editor.colWhat': 'Qué es',
  'editor.colNote': 'Nota',
  'editor.sectionHeading': 'Título de sección',
  'editor.removeHeading': 'Quitar el título',
  'editor.newSection': 'Sección {count}',
  'editor.notePlaceholder': 'picado fino',
  'editor.removeLine': 'Quitar la línea',
  'editor.addLine': 'Añadir una línea',
  'editor.lines_one': '{count} línea',
  'editor.lines_other': '{count} líneas',
  'editor.addSection': 'Añadir una sección',
  'editor.hintNext': 'línea siguiente',
  'editor.hintField': 'campo siguiente',
  'editor.hintMove': 'mover una línea',
  'editor.hintCreate': 'sobre un nombre desconocido lo crea',
  'editor.kindRecipe': 'Receta',
  'editor.kindIngredient': 'Ingrediente',
  'editor.kindNew': 'Nuevo',
  'editor.theRest': 'El resto',
  'editor.batchMakes': 'Una tanda rinde',
  'editor.dishPlates_one': 'Las cantidades son para un plato.',
  'editor.dishPlates_other': 'Las cantidades son para {count} platos.',
  'editor.yieldUnit': 'Unidad de rendimiento',
  'editor.prepMinutes': 'Preparación, minutos',
  'editor.keepsHours': 'Aguanta, horas',
  'editor.storagePlaceholder': 'Al vacío, cámara, etiquetado',
  'editor.tags': 'Etiquetas',
  'editor.tagsPlaceholder': 'pastel, a la carta',
  'editor.mayContain': 'Puede contener · solo contaminación cruzada',
  'editor.rolledUp': 'Sumado a partir de las líneas',
  'editor.rollupUnsaved': 'Guarda para calcular los alérgenos.',
  'editor.rollupContains': 'Contiene {allergens}',
  'editor.rollupNote': 'Según el último guardado, subrecetas incluidas.',
  'editor.rollupNone': 'Nada que declarar debajo, según lo último guardado.',
  'editor.stepFirst': 'Sudar las chalotas en mantequilla sin que tomen color',
  'editor.stepNext': 'El siguiente paso',
  'editor.step': 'Paso {count}',
  'editor.removeStep': 'Quitar el paso {count}',

  'picker.label': 'Qué es esta línea',
  'picker.placeholder': 'Ingrediente o subreceta',
  'picker.create': 'Crear ingrediente',
  'picker.creating': 'Creando',

  'ingredients.subtitle':
    'El rendimiento tras limpiar, la densidad y el peso por pieza permiten que la lista diga cuánto sacar de la cámara. También puedes añadir ingredientes desde el editor de recetas.',
  'ingredients.search': 'Buscar ingredientes',
  'ingredients.new': 'Nuevo ingrediente',
  'ingredients.colYield': 'Rendimiento',
  'ingredients.colDensity': 'g / ml',
  'ingredients.colEach': 'g por unidad',
  'ingredients.noMatch': 'No hay resultados.',
  'ingredients.empty': 'Todavía no hay nada.',
  'ingredients.explainTitle': 'Para qué sirven los tres números',
  'ingredients.yieldTitle': 'Rendimiento.',
  'ingredients.yieldBody':
    'Lo que queda después de pelar y limpiar, como parte de lo que se compra: un kilo de cebollas da unos 900 g aprovechables.',
  'ingredients.densityTitle': 'Gramos por mililitro.',
  'ingredients.densityBody':
    'Permite que una receta escrita en litros dé un peso, y al revés. El aceite ronda 0,92.',
  'ingredients.eachTitle': 'Gramos por unidad.',
  'ingredients.eachBody':
    'Convierte un número de piezas en un peso. Una cebolla ronda 170, un huevo unos 50.',
  'ingredients.byVolume': 'Se mide por volumen',
  'ingredients.counted': 'Se cuenta',
  'ingredients.weighed': 'Se pesa',
  'ingredients.category': 'Categoría',
  'ingredients.categoryPlaceholder': 'Verdura',
  'ingredients.measured': 'Se mide normalmente',
  'ingredients.optWeight': 'Por peso',
  'ingredients.optVolume': 'Por volumen',
  'ingredients.optCount': 'Por unidades',
  'ingredients.yieldLabel': 'Rendimiento, por ciento',
  'ingredients.yieldHint': 'Lo que queda después de pelar y limpiar. La cebolla ronda 90.',
  'ingredients.densityLabel': 'Gramos por mililitro',
  'ingredients.densityHint': 'Solo para pasar de peso a volumen. El aceite ronda 0,92.',
  'ingredients.eachLabel': 'Gramos por unidad',
  'ingredients.eachHint': 'Solo para pasar de unidades a peso. Un huevo ronda 50.',
  'ingredients.allergens': 'Alérgenos',

  'settings.cookOnly':
    'Solo los chefs y los administradores pueden cambiar cómo está montada la cocina.',
  'settings.tabStaff': 'Equipo',
  'settings.tabStations': 'Partidas',
  'settings.tabDevices': 'Dispositivos',
  'settings.tabKitchen': 'Cocina',

  'staff.loadError': 'No se pudo cargar la lista del equipo.',
  'staff.title': 'Equipo',
  'staff.add': 'Añadir a alguien',
  'staff.you': '(tú)',
  'staff.pinTag': 'PIN',
  'staff.passwordTag': 'Contraseña',
  'staff.locked': 'Bloqueado',
  'staff.namePlaceholder': 'Como le conoce la brigada',
  'staff.role': 'Rol',
  'staff.station': 'Partida habitual',
  'staff.noStation': 'Sin partida fija',
  'staff.newPin': 'PIN nuevo',
  'staff.keepIt': 'Déjalo vacío para mantenerlo',
  'staff.pinPlaceholder': '4 cifras',
  'staff.pinHint': 'Para entrar en una tablet de cocina.',
  'staff.passwordSection': 'Acceso con contraseña, para chefs y responsables',
  'staff.newPassword': 'Contraseña nueva',
  'staff.passwordPlaceholder': 'Al menos 12 caracteres',
  'staff.addSubmit': 'Añadir a la brigada',

  'stations.loading': 'Cargando partidas…',
  'stations.loadError': 'No se pudieron cargar las partidas.',
  'stations.title': 'Partidas',
  'stations.body':
    'Las listas se agrupan por partida y se imprimen en este orden, así que ordénalas como está montada la cocina.',
  'stations.empty':
    'Todavía no hay partidas. Una cocina necesita al menos una para poder agrupar una lista.',
  'stations.addLabel': 'Añadir una partida',
  'stations.placeholder': 'Cuarto frío',
  'stations.rename': 'Renombrar',
  'stations.moveEarlier': 'Adelantar {name}',
  'stations.moveLater': 'Atrasar {name}',

  'devices.title': 'Tablets de cocina',
  'devices.body':
    'Los cocineros solo pueden entrar con PIN en una tablet registrada. Regístrala desde la propia tablet, con la sesión de administrador abierta en ella.',
  'devices.enrolled': 'Registrada el {date}',
  'devices.lastUsed': 'último uso el {date}',
  'devices.neverUsed': 'nunca usada',
  'devices.empty': 'Todavía no hay tablets registradas, así que nadie puede entrar con PIN.',
  'devices.enrolLabel': 'Registrar el dispositivo que estás usando',
  'devices.placeholder': 'Tablet del pase',
  'devices.enrolling': 'Registrando…',
  'devices.enrol': 'Registrar este dispositivo',
  'devices.enrolledOk': 'Registrado. Este dispositivo ya puede mostrar el equipo y aceptar PIN.',
  'devices.revokedTitle': 'Revocadas',
  'devices.revokedOn': 'revocada el {date}',
  'devices.revoke': 'Revocar',
  'devices.reallyRevoke': '¿Revocar de verdad?',
  'devices.revokeTitle': 'Cierra al momento la sesión de todos en {name}',

  'kitchen.loadError': 'No se pudieron cargar los ajustes de la cocina.',
  'version.title': 'Versión de Mise',
  'version.running': 'Esta cocina usa Mise {version}.',
  'version.upToDate': 'Es la versión más reciente.',
  'version.newer': 'Ya está Mise {version}.',
  'version.whatChanged': 'Qué ha cambiado',
  'version.checkFailed':
    'No se pudo conectar con GitHub para buscar una versión más nueva. Mise lo intentará mañana.',
  'version.notChecked':
    'Aún no se ha comprobado. Mise mira un minuto después de arrancar y luego una vez al día.',
  'version.checkLabel': 'Buscar una versión más nueva una vez al día',
  'version.checkHint':
    'Mise pregunta a GitHub cuál es la versión más reciente. No se envía nada sobre tu cocina.',
  'version.howTitle': 'Cómo actualizar',
  'version.backupFirst':
    'Descarga antes una copia de seguridad. La base de datos se actualiza sola al arrancar Mise.',
  'version.major':
    '{version} es una nueva versión principal. Lee primero qué ha cambiado y pon MISE_VERSION a {major} en .env, o la etiqueta de la imagen a :{major} en TrueNAS.',
  'version.howTrueNas': 'TrueNAS',
  'version.howTrueNasSteps': 'Abre Apps, elige la app mise y pulsa Update o Pull Images.',
  'version.howInstaller': 'Instalado con el instalador',
  'version.howInstallerSteps':
    'En la carpeta mise-kitchen, vuelve a ejecutar el comando de instalación:',
  'version.howDocker': 'Docker solo',
  'version.howDockerSteps': 'En la carpeta con compose.yaml, ejecuta:',
  'kitchen.title': 'Esta cocina',
  'kitchen.units': 'Unidades',
  'kitchen.metric': 'Métrico — gramos y litros',
  'kitchen.imperial': 'Imperial — onzas y tazas estadounidenses',
  'kitchen.unitsHint':
    'Las unidades con las que empiezan las recetas nuevas. Las existentes conservan las suyas.',
  'kitchen.language': 'Idioma',
  'kitchen.languageHint':
    'El idioma de la aplicación para quien no haya elegido el suyo. Las recetas se quedan tal como se escribieron.',
  'kitchen.retention': 'Líneas que desaparecen de la lista continua',
  'kitchen.retentionKeep': 'Guardarlas en la base de datos, fuera de la vista',
  'kitchen.retentionDelete': 'Eliminarlas para ahorrar espacio',
  'kitchen.retentionHint':
    'Las líneas guardadas registran quién hizo qué y cuándo, para informes futuros, y ocupan espacio. Las eliminadas desaparecen para siempre.',

  'handover.title': 'Relevo',
  'handover.empty': 'Dejar una nota para el siguiente turno',
  'handover.placeholder': 'El pescado llega tarde; poner el fondo a las tres.',
  'handover.save': 'Guardar la nota',
  'handover.clear': 'Borrar la nota',
  'handover.edit': 'Cambiar',
  'handover.remove': 'Quitar la nota',
  'handover.removeConfirm': '¿Quitar?',
  'handover.by': '{name}, {when}',
  'running.title': 'La lista',
  'running.subtitle':
    'Una sola lista para toda la cocina. Las líneas marcadas desaparecen a las dos horas.',
  'running.empty': 'No hay nada en la lista. Añade lo que haya que hacer.',
  'running.merged': 'Sumado a la línea de {name} que ya estaba en la lista.',
  'running.goneAt': 'desaparece a las {time}',

  'task.beingMade': 'Preparándose',
  'task.beingMadeBy': 'Lo está preparando {name}',
  'settings.tabBackups': 'Copias de seguridad',
  'backups.title': 'Copias de seguridad',
  'backups.body':
    'Cada noche Mise guarda toda la cocina, fotos incluidas, en un único archivo cifrado. Guarda también una copia fuera de esta máquina.',
  'backups.lastGood': 'Última copia: {time}',
  'backups.never': 'Aún no hay ninguna copia. La primera se hará en unos minutos.',
  'backups.failed': 'La última copia ha fallado.',
  'backups.reason.bad_key':
    'Una clave de recuperación tiene 32 letras y números, en grupos de cuatro.',
  'backups.reason.wrong_key':
    'Esa clave de recuperación no abre esta copia, o el archivo está dañado.',
  'backups.reason.not_a_backup': 'Ese archivo no es una copia de Mise.',
  'backups.reason.too_new':
    'Esa copia viene de una versión más nueva de Mise. Actualiza primero este servidor.',
  'backups.reason.local': 'No se pudo guardar en esta máquina. Puede que el disco esté lleno.',
  'backups.reason.unreachable':
    'Mise no pudo conectar con el NAS. Revisa la dirección y que el NAS esté encendido.',
  'backups.reason.auth': 'El NAS rechazó el usuario o la contraseña.',
  'backups.reason.folder': 'Esa carpeta no existe en el NAS. Créala allí primero.',
  'backups.reason.remote': 'El NAS se negó a guardar el archivo.',
  'backups.reason.unknown':
    'Algo inesperado ha fallado. El registro del servidor tiene los detalles.',
  'backups.running': 'Haciendo copia…',
  'backups.now': 'Hacer copia ahora',
  'backups.download': 'Descargar',
  'backups.keyTitle': 'Clave de recuperación',
  'backups.keyBody':
    'Una copia solo se abre con esta clave. Imprímela o apúntala, y guárdala lejos de esta máquina. Sin ella, nadie puede abrir una copia.',
  'backups.keyShow': 'Mostrar la clave de recuperación',
  'backups.keyHide': 'Ocultar',
  'backups.keyPrint': 'Imprimir',
  'backups.keyConfirm': 'Ya la he apuntado',
  'backups.keyConfirmed': 'Marcada como apuntada.',
  'backups.keyPrintTitle': 'Clave de recuperación de {kitchen}',
  'backups.keyPrintBody':
    'Con esta clave se restaura una copia de esta cocina en Mise. No la guardes junto al servidor: mejor en la caja fuerte de la oficina o en casa.',
  'backups.whereTitle': 'Adónde van las copias',
  'backups.onlyHereTitle': 'Las copias solo están en esta máquina',
  'backups.onlyHereBody':
    'Si se estropea, la roban o falla su disco, las copias se pierden con ella. Envíalas también a un NAS o a OneDrive.',
  'backups.onlyHereAction': 'Elegir adónde van',
  'backups.whereLocal': 'En esta máquina, siempre',
  'backups.whereLocalHint':
    'Se guardan en la misma máquina que Mise. Si se estropea, la roban o falla su disco, se pierden con ella. Así que descarga una de vez en cuando, o envíalas a un NAS o a OneDrive.',
  'backups.whereWebdav': 'También a un NAS o Nextcloud',
  'backups.whereWebdavHint':
    'Por WebDAV, que ofrecen Synology, QNAP y Nextcloud. También queda una copia en esta máquina.',
  'backups.webdavUrl': 'Dirección de la carpeta',
  'backups.webdavUrlHint':
    'La dirección WebDAV de una carpeta que ya exista. Dentro del restaurante basta con http://.',
  'backups.webdavUser': 'Usuario',
  'backups.webdavPassword': 'Contraseña',
  'backups.webdavPasswordKept': 'Déjalo vacío para mantener la contraseña guardada.',
  'backups.test': 'Probar la conexión',
  'backups.testing': 'Probando…',
  'backups.testOk': 'Conectado. Mise puede escribir en esa carpeta.',
  'backups.keepRemote': 'Guardar en el NAS',
  'backups.keepLocal': 'Guardar en esta máquina',
  'backups.copies_one': '{count} copia',
  'backups.copies_other': '{count} copias',
  'backups.when': 'Copia nocturna a las',
  'backups.zone': 'Zona horaria: {zone}.',
  'backups.kept': 'En esta máquina',
  'backups.keptEmpty': 'Aquí todavía no hay nada.',
  'backups.bannerFailed': 'La última copia ha fallado.',
  'backups.bannerStale': 'Hace dos días que no se hace ninguna copia.',
  'backups.bannerKey':
    'Nadie ha apuntado aún la clave de recuperación. Sin ella no se puede abrir ninguna copia.',
  'backups.bannerAction': 'Ver copias',
  'backups.whereOnedrive': 'También a OneDrive',
  'backups.whereOnedriveHint':
    'En la carpeta Apps/Mise de tu OneDrive. Mise no puede ver nada más en tu OneDrive. También queda una copia en esta máquina.',
  'backups.onedriveConnect': 'Conectar OneDrive',
  'backups.onedriveStarting': 'Preguntando a Microsoft…',
  'backups.onedriveCodeIntro':
    'En un móvil o un ordenador, abre esta página, escribe el código de abajo e inicia sesión con la cuenta de Microsoft cuyo OneDrive debe recibir las copias.',
  'backups.onedriveWaiting': 'Esperando el inicio de sesión…',
  'backups.onedriveUnverified':
    'Microsoft dirá que Mise no está verificada. Es normal en las aplicaciones independientes, y Mise solo pide su propia carpeta.',
  'backups.onedriveConnected': 'Conectado al OneDrive de {account}.',
  'backups.onedriveDisconnect': 'Desconectar',
  'backups.onedrive.declined': 'El inicio de sesión se rechazó en Microsoft.',
  'backups.onedrive.expired':
    'El código caducó antes de que nadie iniciara sesión. Vuelve a empezar.',
  'backups.onedrive.failed': 'Microsoft no aceptó la conexión. Inténtalo de nuevo en un minuto.',
  'backups.keepOnedrive': 'Guardar en OneDrive',
  'backups.keepBoth': 'Guardar en el NAS y en OneDrive',
  'backups.reason.onedrive_auth':
    'OneDrive ya no deja entrar a Mise. Vuelve a conectarlo en la página de copias.',
  'backups.reason.onedrive_full': 'OneDrive está lleno.',
  'backups.reason.onedrive_unreachable':
    'Mise no pudo conectar con OneDrive. Revisa la conexión a internet.',
  'backups.reason.onedrive_failed':
    'OneDrive rechazó la copia. Mise lo volverá a intentar en unas horas.',
  'restore.cta': 'Restaurar una copia',
  'restore.eyebrow': 'Restaurar',
  'restore.title': 'Recuperar una cocina',
  'restore.body':
    'Elige un archivo de copia y escribe la clave de recuperación que se imprimió para ella. Vuelven el equipo, las recetas, las fotos y las listas de mise en place. Después todos vuelven a iniciar sesión.',
  'restore.file': 'Archivo de copia',
  'restore.key': 'Clave de recuperación',
  'restore.keyHint': 'Las mayúsculas y los guiones son opcionales.',
  'restore.fileMissing': 'Elige un archivo de copia.',
  'restore.keyMissing': 'Escribe la clave de recuperación.',
  'restore.submit': 'Restaurar',
  'restore.restoring': 'Restaurando…',
  'restore.working': 'En una máquina pequeña puede tardar un minuto.',
  'restore.done': '{kitchen} ha vuelto',
  'restore.doneBody':
    'Inicia sesión con el mismo correo y la misma contraseña de antes. Las copias se quedan en esta máquina hasta que vuelvas a configurar un NAS en Ajustes.',
  'restore.counts': 'Equipo: {staff} · Recetas: {recipes} · Fotos: {photos}',
  'restore.signIn': 'Iniciar sesión',
  'restore.asideTitle': 'Qué vuelve',
  'restore.asideStaff': 'El equipo, con contraseñas y PIN',
  'restore.asideRecipes': 'Recetas, ingredientes y fotos de emplatado',
  'restore.asidePrep': 'La lista de mise en place',
  'restore.asideTablets': 'Las tablets registradas, si el servidor mantiene su dirección',

  // Dish cards
  'recipe.dishCard': 'Ficha de plato',
  'editor.frontOfHouse': 'Sala',
  'editor.frontOfHouseHint': 'Lo que la ficha de plato le cuenta a la sala.',
  'editor.flavour': 'Perfil de sabor',
  'editor.flavourPlaceholder': 'cremoso, ajo, fresco',
  'editor.temperature': 'Se sirve',
  'editor.course': 'Lugar en el menú',
  'editor.drinks': 'Maridaje',
  'editor.drinksPlaceholder': 'Burdeos blanco, kombucha',
  'editor.cutlery': 'Cubiertos',
  'editor.cutleryPlaceholder': 'cuchara pequeña',
  'editor.listHint': 'Separa con comas.',
  'editor.notSet': 'Sin indicar',
  'temperature.cold': 'Frío',
  'temperature.room': 'Temperatura ambiente',
  'temperature.warm': 'Templado',
  'temperature.hot': 'Caliente',
  'temperature.hot_and_cold': 'Frío y caliente',
  'course.amuse': 'Aperitivo',
  'course.starter': 'Entrante',
  'course.middle': 'Plato intermedio',
  'course.main': 'Plato principal',
  'course.pre_dessert': 'Predessert',
  'course.dessert': 'Postre',
  'course.mignardise': 'Mignardises',
  'course.side': 'Guarnición',
  'ingredients.vegan': 'Vegano',
  'ingredients.veganYes': 'Sí',
  'ingredients.veganNo': 'No',
  'ingredients.veganUnknown': 'Sin indicar',
  'ingredients.veganHint':
    'Una ficha de plato nunca llama vegano a un plato mientras uno de sus ingredientes esté sin indicar.',
  'ingredients.whichCereals': 'Qué cereales',
  'ingredients.whichNuts': 'Qué frutos de cáscara',
  'ingredients.cerealsPlaceholder': 'trigo, centeno',
  'ingredients.nutsPlaceholder': 'avellana, nuez',
  'ingredients.detailsHint':
    'La ley pide que el cereal y el fruto de cáscara figuren por su nombre en la ficha de plato.',
  'card.title': 'Ficha de plato',
  'card.back': '← El plato',
  'card.language': 'Idioma de la ficha',
  'card.theme': 'Aspecto',
  'card.themeDay': 'Día',
  'card.themeNight': 'Noche',
  'card.print': 'Imprimir',
  'card.download': 'Descargar PDF',
  'card.downloading': 'Creando el PDF…',
  'card.downloadFailed': 'No se pudo crear el PDF. Inténtalo de nuevo o imprime.',
  'card.notADish': 'Solo un plato tiene ficha de plato.',
  'card.unavailable':
    'Este plato remite a una receta o ingrediente que ya no existe, así que no se pueden calcular sus alérgenos. Arregla primero la receta.',
  'card.noAccess':
    'En esta cocina solo los chefs y los administradores pueden abrir las fichas de plato.',
  'card.attentionTitle': 'Conviene revisar antes de imprimir',
  'card.attentionVegan':
    'Nadie ha indicado si estos son veganos: {names}. Hasta que alguien lo haga, la ficha no puede decir que el plato es vegano.',
  'card.attentionDetails': 'Estos contienen gluten o frutos de cáscara sin decir cuáles: {names}.',
  'card.attentionPhoto': 'Este plato aún no tiene foto.',
  'card.attentionDraft': 'Esta receta todavía es un borrador.',
  'card.fixIngredients': 'Abrir los ingredientes',
  'card.menu': 'Menú',
  'card.flavour': 'Perfil de sabor',
  'card.temperature': 'Temperatura',
  'card.course': 'Lugar en el menú',
  'card.components': 'Componentes',
  'card.drinks': 'Maridaje',
  'card.cutlery': 'Cubiertos',
  'card.allergens': 'Alérgenos',
  'card.component': 'Componente',
  'card.vegan': 'Vegano',
  'card.other': 'Otros ingredientes del plato',
  'card.keyContains': 'contiene',
  'card.keyMayContain': 'puede contener trazas',
  'card.keyVegan': 'vegano',
  'card.keyUnknown': 'sin indicar',
  'card.cerealsAndNuts': 'Cereales con gluten y frutos de cáscara',
  'card.noCerealsOrNuts': 'Este plato no lleva cereales con gluten ni frutos de cáscara.',
  'card.cereals': 'Cereales: {names}',
  'card.nuts': 'Frutos de cáscara: {names}',
  'card.unnamed': 'sin nombrar aún',
  'card.printed': 'Impreso el {date}',
  'card.page': 'Página {page} de {pages}',
  'card.madeWithMise': 'Hecho con Mise, software libre bajo la AGPL-3.0',
  'card.noAllergens': 'Ninguno de los 14 alérgenos',
  'card.notVegan': 'No vegano',
  'card.veganUnknown': 'Vegano: sin indicar',
  'cardAllergen.eggs': 'Huevo',
  'cardAllergen.gluten': 'Gluten',
  'cardAllergen.lupin': 'Altra\u00admuz',
  'cardAllergen.milk': 'Leche',
  'cardAllergen.mustard': 'Mos\u00adtaza',
  'cardAllergen.sulphites': 'Sul\u00adfitos',
  'cardAllergen.molluscs': 'Molus\u00adcos',
  'cardAllergen.fish': 'Pes\u00adcado',
  'cardAllergen.soybeans': 'Soja',
  'cardAllergen.sesame': 'Sésamo',
  'cardAllergen.celery': 'Apio',
  'cardAllergen.crustaceans': 'Crustá\u00adceos',
  'cardAllergen.peanuts': 'Caca\u00adhuetes',
  'cardAllergen.nuts': 'Frutos de cáscara',
  'settings.tabDishCards': 'Fichas de plato',
  'dishCards.intro':
    'Cómo se ve cada ficha de plato de esta cocina. Abre una ficha desde cualquier plato.',
  'dishCards.managersOnly':
    'Solo los chefs y los administradores pueden cambiar el aspecto de las fichas de plato.',
  'dishCards.titleLabel': 'Encabezado',
  'dishCards.titleHint': 'Déjalo vacío para «{default}».',
  'dishCards.locationName': 'Nombre del local',
  'dishCards.address': 'Dirección',
  'dishCards.logo': 'Logotipo',
  'dishCards.logoHint': 'JPEG, PNG o WebP, hasta 2 MB. Mejor cuadrado.',
  'dishCards.logoUpload': 'Elegir un logotipo',
  'dishCards.logoReplace': 'Sustituir',
  'dishCards.logoRemove': 'Quitar',
  'dishCards.logoNone': 'Aún no hay logotipo.',
  'dishCards.warning': 'Aviso al pie de cada ficha',
  'dishCards.warningHint':
    'Escríbelo en cada idioma en que imprimas. Una ficha en un idioma sin aviso usa el del idioma de la cocina.',
  'dishCards.warningPlaceholder': 'Esta ficha de plato es propiedad de …',
  'dishCards.show': 'En la ficha',
  'dishCards.part.menu': 'Menú',
  'dishCards.part.flavour': 'Perfil de sabor',
  'dishCards.part.temperature': 'Temperatura',
  'dishCards.part.course': 'Lugar en el menú',
  'dishCards.part.drinks': 'Maridaje',
  'dishCards.part.cutlery': 'Cubiertos',
  'dishCards.part.photo': 'Foto',
  'dishCards.part.logo': 'Logotipo',
  'dishCards.part.location': 'Local y dirección',
  'dishCards.part.summary': 'Alérgenos en el anverso',
  'dishCards.part.mayContain': 'Marcas de «puede contener»',
  'dishCards.part.vegan': 'Columna vegano',
  'dishCards.theme': 'Aspecto',
  'dishCards.themeDayHint': 'Blanco, gasta poco tóner.',
  'dishCards.themeNightHint': 'Oscuro, mejor en pantalla o en una láser a color.',
  'dishCards.access': 'Quién puede abrir las fichas de plato',
  'dishCards.accessEveryone': 'Cualquiera que haya iniciado sesión',
  'dishCards.accessManagers': 'Solo chefs y administradores',
  'dishCards.miseLine': 'Cada ficha termina con una pequeña línea que dice que se hizo con Mise.',
  // Menus
  'nav.menus': 'Menús',
  'dishes.subtitle':
    'Cada plato como su ficha: lo que lee la sala, con los alérgenos calculados a partir de cada subreceta.',
  'dishes.new': 'Nuevo plato',
  'dishes.search': 'Buscar un plato',
  'dishes.anyCourse': 'Cualquier pase',
  'dishes.onMenu': 'Solo en un menú activo',
  'dishes.noMatch': 'Ningún plato coincide.',
  'dishes.empty':
    'Aún no hay platos. Un plato es lo que sale al pase: añade uno y aparecerá aquí como su ficha.',
  'dishes.count_one': '{count} plato',
  'dishes.count_other': '{count} platos',
  'dishes.countFreeFrom': '{shown} de {total} platos sin {allergen}',
  'dishes.countFiltered': '{shown} de {total} platos',
  'dishes.notOnMenu': 'En ningún menú',
  'dishes.broken': 'No se pueden calcular los alérgenos: falta una subreceta o se usa a sí misma.',
  'dishes.openSpec': 'Receta',
  'dishes.print': 'Imprimir o PDF',
  'dishes.kept':
    'Esta cocina reserva las fichas de plato a chefs y administradores. La receta sigue abierta para ti.',
  'menus.subtitle':
    'Los platos que la sala vende juntos. Imprime el resumen de alérgenos de un menú, o todas sus fichas de plato a la vez.',
  'menus.name': 'Nombre del menú',
  'menus.namePlaceholder': 'Menú de otoño 2026',
  'menus.new': 'Nuevo menú',
  'menus.empty': 'Aún no hay menús. Empieza uno con un nombre arriba.',
  'menus.dishes_one': '{count} plato',
  'menus.dishes_other': '{count} platos',
  'menuStatus.draft': 'Borrador',
  'menuStatus.active': 'Activo',
  'menuStatus.archived': 'Archivado',
  'menus.back': '← Menús',
  'menus.dishesTitle': 'En el menú',
  'menus.changedCount_one': '{count} cambiado desde que se imprimieron las fichas',
  'menus.changedCount_other': '{count} cambiados desde que se imprimieron las fichas',
  'menus.noDishes': 'Aún no hay platos en este menú.',
  'menus.noCourse': 'Sin pase',
  'menus.changed': 'Cambiado',
  'menus.removeDish': 'Quitar {name} del menú',
  'menus.addDish': 'Añadir el plato',
  'menus.pickDish': 'Elige un plato',
  'menus.allDishesOn': 'Todos los platos ya están en este menú.',
  'menus.status': 'Estado',
  'menus.description': 'Descripción',
  'menus.descriptionPlaceholder': 'Unas palabras para la sala, como el precio o cuándo se sirve.',
  'menus.overview': 'Resumen de alérgenos',
  'menus.cardsTitle': 'Fichas de plato',
  'menus.cardsProgress': 'Ficha {done} de {total}…',
  'menus.allCards': 'Todas las fichas de plato (PDF)',
  'menus.lastPrinted': 'Fichas descargadas por última vez el {when}.',
  'menus.neverPrinted': 'Las fichas de plato de este menú aún no se han descargado.',
  'menus.copyName': 'Nombre de la copia',
  'menus.copyPlaceholder': '{name} (copia)',
  'menus.duplicate': 'Copiar el menú',
  'menus.delete': 'Eliminar el menú',
  'menus.backToMenu': '← El menú',
  'menus.overviewTitle': 'Resumen de alérgenos',
  'menus.dish': 'Plato',
  'access.eyebrow': 'Fuera de la cocina',
  'access.title': 'Pide acceso',
  'access.body':
    'Fuera de la cocina, Mise solo se abre en dispositivos que la cocina ha aprobado. Ponle un nombre a este dispositivo y pide acceso. Un chef o el administrador lo aprueba en Ajustes → Dispositivos.',
  'access.nameLabel': 'Nombre de este dispositivo',
  'access.namePlaceholder': 'p. ej. Móvil de Sam',
  'access.ask': 'Pedir acceso',
  'access.asking': 'Pidiendo…',
  'access.waitingTitle': 'Esperando aprobación',
  'access.waitingBody':
    '“{name}” ha pedido acceso. Pide a un chef o al administrador que lo apruebe en Ajustes → Dispositivos, y léele este código para que sepa que eres tú:',
  'access.codeLabel': 'Código',
  'access.checking': 'Esta página abre Mise sola en cuanto se apruebe.',
  'access.whyTitle': '¿Por qué pedir?',
  'access.whyBody':
    'Las recetas, el equipo y los alérgenos son de la cocina. Pedir primero significa que nadie en internet puede ni siquiera llegar a la página de acceso. La aprobación dura hasta que se retire, o hasta que este dispositivo pase 90 días sin usarse.',
  'outside.title': 'Fuera de la cocina',
  'outside.body':
    'Móviles y portátiles fuera de la red de la cocina o de la VPN. Solo ven una página de solicitud hasta que alguien los aprueba aquí. La aprobación dura hasta que la retires, o hasta que el dispositivo pase 90 días sin usarse.',
  'outside.waiting': 'Esperando aprobación',
  'outside.approved': 'Aprobados',
  'outside.empty':
    'No hay dispositivos de fuera. Quien abra Mise desde fuera de la cocina puede pedir acceso, y su solicitud aparece aquí.',
  'outside.code': 'Código',
  'outside.checkCode': 'Aprueba solo si quien lo pide ve este mismo código en su pantalla.',
  'outside.passwordLabel': 'Tu contraseña',
  'outside.passwordWhy': 'Estás fuera de la cocina, así que aprobar vuelve a pedir tu contraseña.',
  'outside.approve': 'Aprobar',
  'outside.deny': 'Rechazar',
  'outside.approvedBy': 'aprobado por {name} el {date}',
  'outside.approvedByFrom': 'aprobado por {name} desde {device} el {date}',
  'outside.approvedOn': 'aprobado el {date}',
  'outside.thisDevice': 'Este dispositivo',
  'outside.revokeSelf': 'Este dispositivo tendrá que volver a pedir acceso',
  'outside.reallyRevokeSelf': '¿Dejar fuera este dispositivo?',
  'error.accessRequired': 'Este dispositivo no está aprobado para entrar desde fuera de la cocina.',
  'error.wrongPassword': 'Esa contraseña no es correcta.',
  'error.passwordNeeded':
    'Aprobar desde fuera necesita una contraseña, y esta cuenta no tiene. Apruébalo desde la cocina.',
  'error.tooManyRequests': 'Hay demasiados dispositivos esperando aprobación. Inténtalo más tarde.',
  'settings.tabConnection': 'Conexión',
  'connection.relayedTitle': 'Mise no puede saber quién está dentro de la cocina',
  'connection.relayedBody':
    'Algunos visitantes llegan a Mise a través del propio relé de Docker ({address}), que oculta dónde están realmente. Por eso Mise los cuenta como de fuera: cada dispositivo pide acceso una vez y un chef lo aprueba.',
  'connection.relayedTrusted':
    'Algunos visitantes llegan a Mise a través del propio relé de Docker ({address}), que oculta dónde están realmente. Elegiste contarlos como dentro de la cocina, así que se saltan la aprobación que necesitan los dispositivos de fuera, estén donde estén.',
  'connection.relayedTrust': 'Contar a estos visitantes como dentro de la cocina',
  'connection.relayedTrustHint':
    'Solo si a Mise no se puede llegar desde internet, o solo a través de un servidor web de confianza. Si no, cualquiera que lo encuentre se salta la aprobación.',
  'connection.relayedFix':
    'Pasa con Docker sin root, Podman, Docker Desktop, o por IPv6 cuando la red de Docker no lo tiene. Ejecuta Mise con la red del host (network_mode: host), o activa IPv6 en la red de Docker, para que Mise vea la dirección de cada visitante. Consulta «From outside the kitchen» en el README.',
  'connection.domainTitle': 'Dirección desde fuera',
  'connection.domainBody':
    'Si Mise es accesible por internet, escribe la dirección que lleva a él, como mise.tucocina.es. Desde fuera de la cocina, Mise solo responde en esa dirección: la IP sola, o el nombre de otra web en el mismo servidor, no encuentra nada. En la red de la cocina y la VPN, todas las direcciones siguen funcionando.',
  'connection.domainLabel': 'Dominio',
  'connection.domainSet': 'Desde fuera, Mise solo responde en {domain}.',
  'connection.domainUnset':
    'Sin definir: desde fuera, cualquier dirección que llegue a este servidor lleva a Mise.',
  'connection.domainClear': 'Borrar',
  'connection.domainUseCurrent': 'Usar {domain}, la dirección en la que estás',
  'connection.domainShared':
    '¿Otras webs en la misma IP pública? Lo que ya las sirve (Apache, LiteSpeed o Nginx, a menudo configurado con un panel como cPanel o Plesk) tiene que pasar este dominio a Mise, conservando el nombre de dominio en la petición. Si Mise funciona en otra conexión distinta de esas webs, no hace falta nada más.',
  'error.domainLocksYouOut':
    'Estás conectado por otra dirección, así que te quedarías fuera. Configúralo desde la cocina, o abre primero Mise por la nueva dirección.',
  'proxy.title': 'Detrás de otro servidor web',
  'proxy.body':
    '¿Otro servidor web de tu red (Apache, por ejemplo, con tus webs) ya responde por tus dominios? Deja que pase tu dominio de Mise, con su propio certificado. Mise necesita saber qué máquina es, para distinguir quién está en casa y quién fuera.',
  'proxy.trusted': 'Mise cree a {address} sobre quién es cada visitante.',
  'proxy.none':
    'Ningún servidor web de confianza: todo lo que llega a través de otra máquina cuenta como de fuera.',
  'proxy.stop': 'Dejar de confiar',
  'proxy.seen': 'Las peticiones para {host} llegan a través de {address}. ¿Es tu servidor web?',
  'proxy.trust': 'Confiar en {address}',
  'proxy.addressLabel': 'O escribe la dirección del servidor web',
  'proxy.trustTyped': 'Confiar',
  'proxy.apacheTitle': 'Configurar Apache',
  'proxy.miseAddress': 'Mise en tu red',
  'proxy.step1': 'En la máquina de Apache, comprueba que llega a Mise. Debería mostrar "ok":',
  'proxy.step1Bridge':
    '¿Apache en una VM en la misma TrueNAS que Mise y no vuelve nada? Las VM de TrueNAS a menudo no llegan a su propio host. Crea un puente (br0) en Network de TrueNAS, pasa la dirección del host a él y conecta la tarjeta de red de la VM a br0.',
  'proxy.step2': 'Guarda esto como /etc/apache2/sites-available/mise.conf:',
  'proxy.step3': 'Actívalo y consigue su certificado, igual que con tus otras webs:',
  'proxy.step4':
    'Abre https://{domain} una vez y vuelve aquí: Mise muestra la dirección por la que llegó, y confías en ella.',
  'proxy.router':
    'Tu router no cambia: los puertos 80 y 443 ya van a Apache. Apunta el dominio a la IP de tu casa, como tus otras webs.',
  'proxy.copy': 'Copiar',
  'proxy.copied': 'Copiado',
  'error.proxyNotLocal':
    'Usa la dirección de un servidor web de la red de la cocina, como 192.168.1.60.',
  'https.title': 'HTTPS',
  'https.body':
    'El HTTP normal sigue funcionando siempre. HTTPS permite que las tablets sigan trabajando sin conexión e instalen Mise como app, y es imprescindible para todo lo accesible desde internet.',
  'https.on': 'Activado',
  'https.off': 'Desactivado',
  'https.kitchenTitle': 'En la red de la cocina',
  'https.kitchenBody':
    'Mise crea un certificado propio para las direcciones que usan tus tablets. Sin dominio ni internet. Cada tablet confía en él una vez, desde la página de confianza.',
  'https.renews': 'Se renueva solo. El actual vale hasta el {date}.',
  'https.trustEachTablet': 'En cada tablet, abre esta página una vez y sigue los pasos:',
  'https.namesLabel': 'Direcciones que usan las tablets',
  'https.namesHint':
    'La dirección en la barra de las tablets, sin http:// ni puerto. ¿Varias? Sepáralas con comas.',
  'https.making': 'Creando…',
  'https.turnOn': 'Activar',
  'https.turnOff': 'Desactivar',
  'https.updateNames': 'Actualizar direcciones',
  'https.portNote':
    'HTTPS responde en el puerto {port}. En TrueNAS, la app también debe publicar ese puerto.',
  'https.domainTitle': 'Para tu dominio',
  'https.domainTitleFor': 'Para {domain}',
  'https.domainBody':
    'Un certificado gratuito de Let’s Encrypt, en el que confía cualquier navegador sin pasos en el dispositivo. Mise lo obtiene y lo renueva solo.',
  'https.domainNeedsAddress': 'Primero configura arriba la dirección desde fuera.',
  'https.domainBehindProxy':
    'Mise está detrás de tu propio servidor web, que gestiona HTTPS para este dominio. No lo necesitas.',
  'https.domainStep1': '{domain} apunta a tu dirección IP pública.',
  'https.domainStep2':
    'Tu router reenvía el puerto 80 al puerto {http} de Mise, y el 443 al puerto {https}. En un servidor en la nube, la instalación lo hace.',
  'https.domainStep3': 'Luego pide el certificado:',
  'https.getCertificate': 'Obtener certificado',
  'https.asking': 'Preguntando a Let’s Encrypt…',
  'https.askingNote': 'Suele tardar unos segundos, dos minutos como mucho.',
  'https.failedHint':
    'Lo más habitual es que el puerto 80 aún no llegue a Mise, o que el dominio aún no apunte aquí. Let’s Encrypt solo permite unos pocos intentos fallidos por hora.',
  'https.renewFailed': 'La última renovación falló; Mise lo volverá a intentar: {reason}',
  'trust.eyebrow': 'Este dispositivo',
  'trust.title': 'Confía en el certificado de la cocina',
  'trust.body':
    'Una vez por dispositivo. Después Mise se abre aquí por HTTPS, sigue funcionando si se cae la red y se puede instalar como app.',
  'trust.notOn': 'HTTPS aún no está activado. Un administrador lo activa en Ajustes → Conexión.',
  'trust.trusted': 'Este dispositivo confía en él',
  'trust.trustedBody': 'Estás en Mise por HTTPS. A partir de ahora usa esta dirección.',
  'trust.openMise': 'Abrir Mise',
  'trust.system.ios': 'iPad / iPhone',
  'trust.system.android': 'Android',
  'trust.system.windows': 'Windows',
  'trust.system.mac': 'Mac',
  'trust.system.other': 'Otro',
  'trust.download': 'Descarga el certificado de la cocina.',
  'trust.downloadIos':
    'En Safari, descarga el certificado y permítelo cuando pregunte por un perfil de configuración.',
  'trust.downloadButton': 'Descargar certificado',
  'trust.ios1':
    'Abre Ajustes, toca “Perfil descargado” arriba, luego Instalar, e introduce tu código.',
  'trust.ios2': 'Ve a Ajustes → General → Información → Ajustes de confianza de certificados.',
  'trust.ios3': 'Activa la confianza total para “Mise”.',
  'trust.android1':
    'Abre Ajustes y busca “certificado de CA” (a menudo en Seguridad → Cifrado y credenciales → Instalar un certificado).',
  'trust.android2':
    'Elige “Certificado de CA”, toca “Instalar de todos modos” y selecciona mise-kitchen.crt en tus descargas.',
  'trust.windows1':
    'Abre el archivo descargado, elige “Instalar certificado” y luego “Usuario actual”.',
  'trust.windows2':
    'Elige “Colocar todos los certificados en el siguiente almacén”, selecciona “Entidades de certificación raíz de confianza”, termina y reinicia el navegador.',
  'trust.mac1':
    'Abre el archivo descargado. Acceso a Llaveros lo añade a tu llavero de inicio de sesión.',
  'trust.mac2':
    'Haz doble clic en “Mise”, abre Confiar y pon “Al usar este certificado” en “Confiar siempre”.',
  'trust.other1':
    'Añade el archivo a las autoridades de certificación de confianza del sistema o del navegador. Firefox tiene su propia lista: Ajustes → Privacidad y seguridad → Certificados → Autoridades → Importar.',
  'trust.check':
    'Comprueba que funcionó: esto abre esta página por HTTPS. Si sale un aviso, faltó un paso.',
  'trust.checkButton': 'Comprobar',
  'trust.fingerprint': 'Huella, para comparar',
  'error.badName':
    'No es una dirección ni un nombre de host. Usa algo como 192.168.1.50 o mise.local.',
  'error.noDomain': 'Primero configura la dirección desde fuera.',
  'offline.title': 'Sin conexión.',
  'offline.body':
    'Mise no responde desde las {time}. Ves lo que tenía este dispositivo, y los cambios se guardan aquí hasta que Mise vuelva a responder.',
  'offline.waiting_one': '1 cambio esperando.',
  'offline.waiting_other': '{count} cambios esperando.',
  'offline.sending_one': 'Enviando 1 cambio hecho sin conexión…',
  'offline.sending_other': 'Enviando {count} cambios hechos sin conexión…',
  'offline.dropped': 'No se pudo guardar {what}: {reason}',
  'offline.setAside':
    '{what} no se pudo guardar: Mise tuvo un problema con ello. Los demás cambios siguen.',
  'offline.tryAgain': 'Reintentar',
  'offline.letGo': 'Descartar',
  'offline.lineWaiting': 'Esperando envío',
  'offline.allergensOnceSent': 'Los alérgenos se actualizarán cuando se envíen tus cambios.',
  'offline.cardOnceSent': 'Esta ficha se actualizará cuando se envíen tus cambios.',
  'offline.menuOnceSent':
    'Los alérgenos y las fichas de plato se actualizarán cuando se envíen tus cambios.',
  'offline.conflictTitle': 'Cambiado en otro dispositivo',
  'offline.conflictBody':
    'Mientras estabas sin conexión, otra persona cambió {what}. ¿Qué versión se queda?',
  'offline.yours': 'La tuya',
  'offline.theirs': 'Ahora en Mise',
  'offline.keepMine': 'Quedarme con la mía',
  'offline.keepTheirs': 'Quedarme con la de Mise',
  'offline.items_one': '1 elemento',
  'offline.items_other': '{count} elementos',
  'offline.field.note': 'Nota',
  'offline.field.notes': 'Notas',
  'offline.field.method': 'Elaboración',
  'offline.field.description': 'Descripción',
  'offline.field.tags': 'Etiquetas',
  'offline.field.components': 'Componentes',
  'offline.field.dishes': 'Platos',
  'offline.field.type': 'Tipo de receta',
  'offline.field.yield': 'Rinde',
  'offline.field.portions': 'Raciones',
  'offline.field.prepMinutes': 'Tiempo de preparación',
  'offline.field.cookMinutes': 'Tiempo de cocción',
  'offline.field.keeps': 'Se conserva',
  'offline.field.allergens': 'Alérgenos',
  'offline.field.mayContain': 'Puede contener',
  'offline.yes': 'Sí',
  'offline.no': 'No',
  'offline.unknownStation': 'Una partida ya eliminada',
  'offline.label.note': 'la nota de relevo',
  'offline.label.addLine': 'la línea de mise en place “{name}”',
  'offline.label.editLine': 'un cambio en una línea de mise en place',
  'offline.label.removeLine': 'la eliminación de una línea',
  'offline.label.recipe': 'la receta “{name}”',
  'offline.label.menu': 'el menú “{name}”',
  'offline.label.ingredient': 'el ingrediente “{name}”',
  'error.offline':
    'Ahora no se puede conectar con Mise, y esto necesita la conexión. Vuelve a intentarlo cuando vuelva.',
  'prepPrint.print': 'Imprimir',
  'prepPrint.title': 'Mise en place',
  'prepPrint.empty': 'Nada en la lista.',
  'prepPrint.line': '{name}, {amount}',
  'recipeCard.makes': 'Rinde',
  'recipeCard.prepTime': 'Preparación',
  'recipeCard.keeps': 'Se conserva',
  'recipeCard.days_one': '{count} día',
  'recipeCard.days_other': '{count} días',
  'recipeCard.hours_one': '{count} hora',
  'recipeCard.hours_other': '{count} horas',
  'recipeCard.mayContain': 'Puede contener: {allergens}',
  'recipeCard.onThePlate': 'En el plato',
  'recipeCard.plating': 'Emplatado',
  'recipeCard.ownRecipe': 'su propia receta',
  'updated.title': 'Mise se ha actualizado',
  'updated.body':
    'Mise se ha actualizado. Recarga para usar la nueva versión; no se pierde nada pendiente de enviar.',
  'updated.bodyVersion':
    'Mise se ha actualizado a {version}. Recarga para usarla; no se pierde nada pendiente de enviar.',
  'updated.reload': 'Recargar',
  'error.pageTitle': 'Esta página no se abrió',
  'error.pageBody': 'Algo salió mal al abrirla. Recargar suele solucionarlo.',
  'access.waitingOne': '“{name}” pide acceso desde fuera de la cocina.',
  'access.waitingCount_one': '1 dispositivo pide acceso desde fuera de la cocina.',
  'access.waitingCount_other': '{count} dispositivos piden acceso desde fuera de la cocina.',
  'access.checkCode': 'Comprobar el código y aprobar',
  'error.incompatibleUnits':
    '{child} rinde en {makes}, así que aquí no se puede medir en {unit}. Usa el mismo tipo de unidad, o cambia lo que rinde {child}.',
  'error.incompatibleUnitsUsedIn':
    '{usedIn} usa {child} en {unit}, y eso no se puede calcular a partir de {makes}. Cambia primero esa línea, o mantén el rendimiento en el mismo tipo de unidad.',
  'recipe.shownAsWritten':
    'Esta receta no se puede calcular para la cantidad pedida, así que se muestra tal como está escrita.',
  'passwordOnly.signIn': 'Iniciar sesión con contraseña',
  'passwordOnly.staff':
    'Has iniciado sesión con tu PIN. Para añadir personal o cambiar roles, contraseñas y PIN, inicia sesión con tu contraseña.',
  'passwordOnly.devices':
    'Has iniciado sesión con tu PIN. Para registrar tablets o dejar entrar dispositivos de fuera, inicia sesión con tu contraseña.',
  'passwordOnly.connection':
    'Has iniciado sesión con tu PIN. Para cambiar cómo se accede a Mise, inicia sesión con tu contraseña.',
  'passwordOnly.backups':
    'Has iniciado sesión con tu PIN. Para gestionar las copias o ver la clave de recuperación, inicia sesión con tu contraseña.',
  'error.passwordRequired':
    'Inicia sesión con tu contraseña para hacer esto. Con un PIN puedes hacer todo lo demás.',
};
